import React, { useCallback, useEffect, useState } from 'react';
import moment from 'moment';
import { MaintenanceReason } from '../../api/Common/CommonTypes';
import './MaintenancePage.css';

interface MaintenancePageProps {
    reason?: MaintenanceReason | null;
    message?: string;
    endsAt?: string | null;
    onRetry?: () => Promise<void> | void;
}

const COPY: Record<MaintenanceReason, { title: string; message: string }> = {
    maintenance: {
        title: 'Scheduled Maintenance',
        message: 'The All Access system is undergoing scheduled maintenance and will be unavailable. Thank you for your patience as we improve our system performance!',
    },
    unavailable: {
        title: 'Temporary Maintenance',
        message: 'The All Access system is undergoing maintenance and is temporarily unavailable. All web site and portal/BoardVue access will be restored as soon as the maintenance is complete. Thank you for your patience as we improve our system performance.',
    },
    offline: {
        title: 'No Internet Connection',
        message: 'All Access requires an Internet connection. Please check your Internet and try again.',
    },
};

const COOLDOWN_SECONDS = 15;
const OFFLINE_RECHECK_SECONDS = 5;

const plural = (count: number, unit: string) => `${count} ${unit}${count === 1 ? '' : 's'}`;

/** How long is left, in words. Null once the end has passed or was never given. */
const timeRemaining = (endsAt?: string | null): string | null => {
    const minutes = endsAt ? moment.utc(endsAt).diff(moment.utc(), 'minutes') : 0;

    if (minutes <= 0) return null;
    if (minutes < 60) return plural(minutes, 'minute');

    const hours = plural(Math.floor(minutes / 60), 'hour');
    const rest = minutes % 60;

    return rest === 0 ? hours : `${hours} and ${plural(rest, 'minute')}`;
};

const MaintenancePage: React.FC<MaintenancePageProps> = ({ reason, message, endsAt, onRetry }) => {
    const copy = COPY[reason ?? 'maintenance'];

    // An unplanned outage has no message of its own; a leftover one would mislead.
    const shown = reason === 'unavailable' || reason === 'offline' ? copy.message : (message || copy.message);

    const [remaining, setRemaining] = useState(() => timeRemaining(endsAt));
    const [retrying, setRetrying] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        setRemaining(timeRemaining(endsAt));
        if (!endsAt) return;

        const timer = setInterval(() => setRemaining(timeRemaining(endsAt)), 60000);
        return () => clearInterval(timer);
    }, [endsAt]);

    useEffect(() => {
        if (reason !== 'offline' || !onRetry) return;

        const handleOnline = () => void onRetry();
        window.addEventListener('online', handleOnline);

        const timer = setInterval(handleOnline, OFFLINE_RECHECK_SECONDS * 1000);

        return () => {
            window.removeEventListener('online', handleOnline);
            clearInterval(timer);
        };
    }, [reason, onRetry]);

    useEffect(() => {
        if (cooldown <= 0) return;

        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    // Out for a cooldown after every attempt: a click right after the last one
    // can only get the same answer back.
    const handleRetry = useCallback(async () => {
        if (!onRetry || retrying || cooldown > 0) return;

        setRetrying(true);
        try {
            await onRetry();
        } finally {
            setRetrying(false);
            setCooldown(COOLDOWN_SECONDS);
        }
    }, [onRetry, retrying, cooldown]);

    return (
        <div className="maintenance-container">
            <div className="maintenance-content">
                {!imageFailed && (
                    <div className="illustration-wrapper">
                        <img src="/Maintenance-Illustration.png" alt="Maintenance Illustration" className="maintenance-image" onError={() => setImageFailed(true)} />
                    </div>
                )}
                <h1 className="maintenance-title">{copy.title}</h1>
                <p className="maintenance-message">{shown}</p>
                {remaining && <p className="maintenance-countdown">Expected return in about {remaining}.</p>}
                {onRetry && (
                    <button type="button" className="maintenance-retry" onClick={handleRetry} disabled={retrying || cooldown > 0}>
                        {retrying ? 'Checking…' : cooldown > 0 ? `Try again in ${cooldown}s` : 'Try again'}
                    </button>
                )}
            </div>
        </div>
    );
};

export default MaintenancePage;
